/**
 * LinguistPro Elite Type Definitions
 * Comprehensive type system for transcription pipeline
 */

import { SystemStatusType } from '../config/constants';

/** Application processing states */
export enum AppStep {
  IDLE = 'IDLE',
  FETCHING = 'FETCHING',
  UPLOADING = 'UPLOADING',
  IDENTIFYING_SPEAKERS = 'IDENTIFYING_SPEAKERS',
  TRANSCRIBING = 'TRANSCRIBING',
  RECONCILING = 'RECONCILING',
  REFINING = 'REFINING',
  INTELLIGENCE_GEN = 'INTELLIGENCE_GEN',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}

/** Log entry severity types */
export type LogType = 'info' | 'success' | 'warning' | 'error';

/** Input source type */
export type InputType = 'local' | 'url';

/** Export format options */
export type ExportFormat = 'docx' | 'srt' | 'clipboard';

/** Individual log entry */
export interface TranscriptionLog {
  id: string;
  timestamp: string;
  message: string;
  type: LogType;
}

/** Analysis output options */
export interface OutputDirectives {
  executiveSummary: boolean;
  meetingSentiment: boolean;
  actionItems: boolean;
  fullForensicAudit: boolean;
  speakerTone: boolean;
  // SPEC-014: Enhanced AI Intelligence types
  /** Formal meeting minutes in British English (SPEC-014) */
  meetingMinutes: boolean;
  /** Key decisions table with attribution and timestamps (SPEC-014) */
  keyDecisions: boolean;
  /** Professional follow-up email draft in British English (SPEC-014) */
  followupEmail: boolean;
  /** Structured action items with assignee, priority, discussed_at (SPEC-014) */
  actionItemsV2: boolean;
  /** Multilingual meeting summary via Claude Opus — responds in the transcript's language */
  multilingualSummary: boolean;
  // SPEC-031: correctDutch and enforceLanguage removed — Dutch correction is now automatic
  // (applied whenever the STT engine returns language='af' or language='nl'), and intelligence
  // synthesis always outputs in British English via system instruction (no user toggle needed).
}

/** Visual scene description from Gemini video transcription */
export interface VideoSceneDescription {
  timestamp: string;
  timestampSecs: number;
  description: string;
}

/** Word-level data from STT — used for review-mode confidence highlighting */
export interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  confidence: number;
  punctuated_word?: string;
}

/** Utterance-level data from STT — used for format modes and review highlighting */
export interface TranscriptUtterance {
  start: number;
  end: number;
  transcript: string;
  speaker: number;
  confidence: number;
  words: TranscriptWord[];
}

/** View modes for the TranscriptViewer */
export type TranscriptViewMode = 'dialogue' | 'paragraph' | 'verbatim' | 'review';

/** Speaker identification data */
export interface SpeakerProfile {
  name: string;
  role?: string;
  identificationSource: string;
}

/** Core transcription state */
export interface TranscriptionState {
  step: AppStep;
  progress: number;
  systemStatus: SystemStatusType;
  error: string | null;
  logs: TranscriptionLog[];
  finalTranscript: string;
  intelligence: string;
  speakerMap: string;
  selectedModelId: string;
  /** Speaker names detected from video frames via Gemini vision. Keys are speaker indices. */
  suggestedSpeakerNames: Record<number, string>;
  /** True while background visual speaker ID is running — blocks export until fully complete. */
  visualIdPending: boolean;
  /** Average word-level confidence from the STT engine (0–1). null = unavailable. */
  confidence: number | null;
  /** A/B/C/D quality grade computed server-side. null = not yet computed. */
  quality_grade: string | null;
  /** Utterance-level data from STT — enables confidence highlighting in review mode. */
  utterances: TranscriptUtterance[] | null;
  /** Per-type intelligence outputs from SPEC-014 enhanced types (e.g. 'meeting-minutes', 'key-decisions') */
  intelligenceOutputs: Record<string, string>;
  /** DB id of the most recently saved transcription — used for regenerate/save-intel */
  savedTranscriptionId: string | null;
  // ── Plan 6: TTS + Voice Q&A ────────────────────────────────────────────
  /** Base64 WAV audio per intelligence tab (keyed by tab id) */
  ttsAudioUrls: Record<string, string>;
  /** True while TTS is loading for a given tab */
  ttsLoading: Record<string, boolean>;
  /** Visual scene descriptions from Gemini video transcription — null for audio-only jobs */
  sceneDescriptions: VideoSceneDescription[] | null;
}

/** Transcription store actions */
export interface TranscriptionActions {
  setStep: (step: AppStep) => void;
  setProgress: (progress: number) => void;
  setSystemStatus: (status: SystemStatusType) => void;
  setError: (error: string | null) => void;
  addLog: (message: string, type: LogType) => void;
  clearLogs: () => void;
  setFinalTranscript: (transcript: string) => void;
  setIntelligence: (intel: string) => void;
  setSpeakerMap: (map: string) => void;
  setSelectedModelId: (modelId: string) => void;
  setSuggestedSpeakerNames: (names: Record<number, string>) => void;
  setVisualIdPending: (pending: boolean) => void;
  setConfidence: (confidence: number | null) => void;
  setQualityGrade: (grade: string | null) => void;
  setUtterances: (utterances: TranscriptUtterance[] | null) => void;
  setIntelligenceOutputs: (outputs: Record<string, string>) => void;
  setSavedTranscriptionId: (id: string | null) => void;
  // ── Plan 6: TTS + Voice Q&A ────────────────────────────────────────────
  /** Store base64 WAV audio for a given intelligence tab */
  setTtsAudio: (tabId: string, audioBase64: string) => void;
  /** Set loading state for a given intelligence tab's TTS request */
  setTtsLoading: (tabId: string, loading: boolean) => void;
  setSceneDescriptions: (scenes: VideoSceneDescription[] | null) => void;
  reset: () => void;
}

/** Combined store type */
export type TranscriptionStore = TranscriptionState & TranscriptionActions;

/** Audio segment for processing */
export interface AudioSegment {
  start: number;
  end: number;
  base64Data: string;
}

/** Audio processing result */
export interface AudioProcessingResult {
  buffer: AudioBuffer;
  duration: number;
}

/** Transcription pipeline options */
/** Supported transcription languages */
export type TranscriptionLanguage = 'en' | 'af';

export interface TranscriptionOptions {
  file: File | null;
  /** Original source file (video) before audio extraction — used for storage upload when WAV exceeds bucket limit */
  originalFile?: File | null;
  remoteUrl: string;
  inputType: InputType;
  directives: OutputDirectives;
  customInstructions: string;
  modelId: string;
  /** Title saved to DB — defaults to file name without extension */
  title?: string;
  /** @deprecated Language is now auto-detected by Gemini. Kept for backwards compat. */
  language?: TranscriptionLanguage;
  /**
   * Languages explicitly confirmed by the user in the pre-transcription prompt.
   * Used to reliably gate post-processing passes (e.g. Dutch→Afrikaans correction)
   * independent of what the STT engine auto-detects — critical for code-switching
   * recordings where the engine may report the dominant language only.
   *
   * Example: ['en', 'af'] for an Afrikaans+English meeting.
   */
  meetingLanguages?: string[];
}

/** Document export options */
export interface ExportOptions {
  outputFileName: string;
  speakerMap: string;
  intelligence: string;
  finalTranscript: string;
}

/** Directive item for UI */
export interface DirectiveItem {
  id: keyof OutputDirectives;
  label: string;
  desc: string;
  hint?: string;
}

/** Status indicator props */
export interface StatusProps {
  status: 'neutral' | 'active' | 'warning';
}

/** Log callback type for services */
export type LogCallback = (message: string, type: LogType) => void;

// ── Auth Types ───────────────────────────────────────────────

export type PlanType = 'free' | 'payg' | 'pro' | 'team' | 'enterprise';

/** User profile from lp_user_profiles table */
export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  first_name?: string | null;
  last_name?: string | null;
  country?: string | null;
  plan: PlanType;
  balance_seconds: number;
  /** Seconds of the balance paid for before the pay-as-you-go minimum (migration 031); absent before it is applied. */
  legacy_paid_seconds?: number | null;
  /** When the customer acknowledged the pay-as-you-go minimum notice; absent/null until they do. */
  payg_floor_notice_ack_at?: string | null;
  onboarded: boolean;
  // Settings (SPEC-008, expanded SPEC-029, SPEC-031: null = auto-detect)
  default_language?: 'en' | 'af' | 'zu' | 'xh' | 'st' | 'tn' | 've' | null;
  default_diarise?: boolean | null;
  notify_low_balance?: boolean | null;
  notify_job_complete?: boolean | null;
  notify_monthly?: boolean | null;
  // Settings (SPEC-024)
  default_engine?: 'auto' | 'assemblyai' | 'deepgram' | 'groq' | null;
  // Organisation (SPEC-021)
  org_id?: string | null;
  created_at: string;
  updated_at: string;
}
